import React, { useEffect, useState } from "react";
import { ThemeProvider } from "styled-components";
import { get, merge } from "lodash";

import AppContext from "./context/AppContext";

import { StringPair } from "./api/generated";
import client from "./api/client/SdsWebApiClient";

import { IntroConfig } from "./models/IntroConfig";

import { AppLoader } from "./components/Loader";
import { ModelSelector } from "./components/ModelSelector";

import {
    filterSetsRecursive,
    getAccountId,
    getApplicationSetting,
    getApplicationSettings,
    getAppVersion,
    getConfigFile,
    getFrontendSettings,
    getLanguage,
    getStylingFile,
} from "./utils/dataUtils";
import { BreakpointProvider } from "./utils/breakpoints";

import defaultAppConfig from "./defaultAppConfig.json";
import "./App.css";
import { activateArticleNumber, goToConfigurator } from "./utils/navigationUtils";
import GaReporter from "./utils/gaReporter";

function App() {
    const [settings, setSettings] = useState<StringPair[]>([]);
    const [introConfig, setIntroConfig] = useState<IntroConfig>();
    const [theme, setTheme] = useState<any>();
    const [translatedTexts, setTranslatedTexts] = useState({});
    const [isLoading, setLoading] = useState(true);
    const [account, setAccount] = useState<string | null>(null);

    const handleDeepLinkingRedirect = (searchParams: URLSearchParams): boolean => {
        const config = searchParams.get("config_string") || searchParams.get("encoded_config");
        if (!config) {
            return false;
        }

        const encoding = searchParams.get("enc_type") || undefined;
        goToConfigurator(config, encoding);
        return true;
    };

    const initStatistics = async (frontendSettings: any, appVersion: string, accountId: string | null) => {
        if (!frontendSettings.ga_tracking_enabled) {
            return;
        }

        // Allow reporting both to a GA property common for all DS apps, and a GA property
        // for a specific customer which we can share to them.
        const trackingIds = [frontendSettings.ga_common_measurement_id, frontendSettings.ga_measurement_id].filter(
            id => !!id
        );
        if (trackingIds.length === 0) {
            return;
        }

        // Set the application base URL as cookie path, removing "model_selector/".
        const siteUrl = new URL(window.location.href);
        const cookiePath = siteUrl.pathname.replace(/\/[^/]+\/?$/i, "") || "/";
        const useCookies = (siteUrl.searchParams.get("allow_statistical_cookies") || "false").toLowerCase() === "true";
        const applicationType = siteUrl.searchParams.get("ga_application_type") || "Web";
        GaReporter.initTracking(trackingIds, useCookies, cookiePath, applicationType, appVersion);
        if (accountId) GaReporter.setAccountId(accountId);
        if (frontendSettings.ga_tracking_project) GaReporter.setProject(frontendSettings.ga_tracking_project);
    };

    useEffect(() => {
        const init = async () => {
            const insideIframe = window.self !== window.top;
            const url = new URL(window.location.href);
            if (insideIframe && document.referrer) {
                const referrerUrl = new URL(document.referrer);
                const refererIsConfigurator =
                    url.hostname.toUpperCase() === referrerUrl.hostname.toUpperCase() &&
                    /configurator\/?$/i.test(referrerUrl.pathname);

                if (!refererIsConfigurator && handleDeepLinkingRedirect(referrerUrl.searchParams)) {
                    return;
                }
            }

            if (!insideIframe && handleDeepLinkingRedirect(url.searchParams)) {
                return;
            }

            const isTouchScreen = url.searchParams.get("touch_screen");
            if (isTouchScreen && /true/i.test(isTouchScreen)) {
                window.addEventListener("contextmenu", event => event.preventDefault());
            }

            // Start simultaneous startup requests as soon as possible.
            const languagePromise = getLanguage();
            getApplicationSettings().then(response => setSettings(response));
            const accountIdPromise = getAccountId();
            const language = await languagePromise;
            const accountId = await accountIdPromise;

            const configurationFiles = await client.getConfigurationFilesGet(language, accountId);

            // Load and set the styling as soon as possible to update the spinner color/background.
            getStylingFile(configurationFiles.stylingFile).then(appConfig => {
                const config = merge(defaultAppConfig, appConfig);
                const langTexts = get(config, `translations.${language}`);
                setTranslatedTexts(langTexts);
                setTheme(config.style);

                // Override CSS variables
                const r = document.documentElement;
                r.style.setProperty("--primary-color", config.colors.primaryColor);
                r.style.setProperty("--primary-hover-color", config.colors.primaryHoverColor);
                r.style.setProperty("--primary-active-color", config.colors.primaryActiveColor);
                r.style.setProperty("--light-hover-color", config.colors.lightHoverColor);
                r.style.setProperty("--light-active-color", config.colors.lightActiveColor);
            });

            const initFilePromise = getConfigFile(configurationFiles.initFile);
            const frontendSettingsPromise = getFrontendSettings();
            const appVersionPromise = getAppVersion();

            // Wait until having the account id before setting the introConfig
            // so we request images for the right account (if set).
            Promise.all([initFilePromise, frontendSettingsPromise, appVersionPromise]).then(
                async ([config, frontendSettings, appVersion]) => {
                    // eslint-disable-next-line no-console
                    console.log(`Model selector version: ${appVersion}`);

                    // Filter any sets by the URL parameter "settings" if present.
                    const setFilter = url.searchParams.get("settings")?.split(",");
                    const filteredConfig = config;
                    if (setFilter && setFilter.length > 0) {
                        filteredConfig.sets = filterSetsRecursive(config.sets, setFilter);
                    }

                    setAccount(accountId);
                    initStatistics(frontendSettings, appVersion, accountId);
                    GaReporter.trackPageView();

                    // If the surface_product parameter is set, activate the first set with that product.
                    const autoPickProduct = url.searchParams.get("surface_product");
                    if (
                        autoPickProduct &&
                        (await activateArticleNumber(
                            autoPickProduct,
                            filteredConfig,
                            accountId || undefined,
                            setFilter
                        ))
                    ) {
                        return;
                    }

                    // Set intro config to start loading images.
                    setIntroConfig(filteredConfig);

                    // Handle external postMessage calls to the iframe.
                    const handleMessage = (event: MessageEvent) => {
                        if (event.data.type === "ACTIVATE_BY_ARTICLE_NUMBER" && event.data.articleNumber) {
                            activateArticleNumber(
                                event.data.articleNumber,
                                filteredConfig,
                                accountId || undefined,
                                setFilter
                            );
                        }
                    };
                    window.addEventListener("message", handleMessage);
                }
            );
        };
        init();
    }, []);

    return settings.length && introConfig && theme ? (
        <ThemeProvider theme={theme}>
            <BreakpointProvider>
                <AppContext.Provider value={{ translatedTexts, isLoading, setIsLoading: setLoading }}>
                    {isLoading ? (
                        <AppLoader
                            color={theme ? get(theme, "appLoader.color", "var(--primary-color)") : "#888"}
                            background={get(theme, "appLoader.backgroundColor", "white")}
                            height={200}
                            width={80}
                        />
                    ) : (
                        <></>
                    )}
                    <div className="App" style={{ background: get(theme, "modelSelector.backgroundColor", "white") }}>
                        <ModelSelector
                            composedUrl={getApplicationSetting(settings, "application.composed_url")}
                            guiResourcesUrl={getApplicationSetting(settings, "application.gui_url")}
                            introConfig={introConfig}
                            accountId={account}
                        />
                    </div>
                </AppContext.Provider>
            </BreakpointProvider>
        </ThemeProvider>
    ) : (
        <AppLoader
            color={theme ? get(theme, "appLoader.color", "var(--primary-color)") : "#888"}
            background={get(theme, "appLoader.backgroundColor", "white")}
            height={200}
            width={80}
        />
    );
}

export default App;
